import { FC, useEffect, useState } from 'react';
import {
  DailyBalanceChart,
  DailyTradeChart,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { BalanceStackedData, StackedData, TViewAccountStatsRes } from '@src/types';
import {
  viewAccountStats,
} from '@src/services/payout';
import dayjs from 'dayjs';

interface AccountStatsDialogProps {
  open: boolean;
  accountId: number | null;
  accountNo?: string;
  onOpenChange: (open: boolean) => void;
}

const formatMoney = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const normalized = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(normalized)) {
    return String(value);
  }

  return `$${normalized.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const toNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const normalized = typeof value === 'string' ? Number(value) : value;

  return Number.isNaN(normalized) ? 0 : normalized;
};

const formatDateTimeLabel = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  return dayjs(value).isValid()
    ? dayjs(value).format('DD MMMM YYYY, HH:mm:ss')
    : '-';
};

const getAutoLiquidatedMessage = (
  stats: TViewAccountStatsRes | null
): string | null => {
  if (!stats) {
    return null;
  }

  const trimmedNotes = stats.notes?.trim();

  if (trimmedNotes) {
    return trimmedNotes;
  }

  if (stats.auto_liquidated_on) {
    return `Auto-liquidated on ${formatDateTimeLabel(stats.auto_liquidated_on)}`;
  }

  return null;
};

const getTradingDaysBadgeClass = (tradingDays?: number): string => {
  return (tradingDays ?? 0) >= 10
    ? 'bg-green-500 text-white px-2 py-0.5 rounded'
    : 'bg-red-500 text-white px-2 py-0.5 rounded';
};

const getNormalizedStatus = (status?: string | null): 'closed' | 'active' => {
  const rawStatus = (status ?? '').toLowerCase();

  return rawStatus.includes('closed') ? 'closed' : 'active';
};

const getStatusBadgeClass = (status?: string | null): string => {
  return getNormalizedStatus(status) === 'closed'
    ? 'bg-red-500 text-white px-2 py-0.5 rounded uppercase'
    : 'bg-green-500 text-white px-2 py-0.5 rounded uppercase';
};

export const AccountStatsDialog: FC<AccountStatsDialogProps> = ({
  open,
  accountId,
  accountNo,
  onOpenChange,
}) => {
  const [statsData, setStatsData] = useState<TViewAccountStatsRes | null>(null);
  const [tradeData, setTradeData] = useState<StackedData[]>([]);
  const [balanceData, setBalanceData] = useState<BalanceStackedData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !accountId) {
      setStatsData(null);
      setTradeData([]);
      setBalanceData([]);

      return;
    }

    const fetchDialogData = async () => {
      setLoading(true);
      try {
        const statsRes = await viewAccountStats(accountId);

        setStatsData(statsRes.data);
        setTradeData(statsRes.data.daily_trade_volumes_and_contracts ?? []);
        setBalanceData(
          (statsRes.data.daily_profit_loss_and_balance ?? []).map((item) => ({
            ...item,
            account_balance: toNumber(item.account_balance),
            net_pl: toNumber(item.net_pl),
          }))
        );
      } catch (error) {
        console.error('Failed to fetch account stats dialog data:', error);
        setStatsData(null);
        setTradeData([]);
        setBalanceData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDialogData();
  }, [open, accountId]);

  const accountLabel = statsData?.account_no ?? accountNo ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'max-w-[980px] w-[98%]'} size={'xl'}>
        <DialogHeader>
          <DialogTitle>Account {accountLabel}</DialogTitle>
        </DialogHeader>
        <div className={'space-y-4'}>
          <div className={'flex gap-6 text-sm'}>
            <div>
              <span className={'text-gray-400'}>Active Trading Days: </span>
              <span className={getTradingDaysBadgeClass(statsData?.trading_days)}>
                {statsData?.trading_days ?? 0}
              </span>
            </div>
            <div>
              <span className={'text-gray-400'}>Status: </span>
              <span className={getStatusBadgeClass(statsData?.status)}>
                {getNormalizedStatus(statsData?.status)}
              </span>
            </div>
            <div>
              <span className={'text-gray-400'}>Balance: </span>
              <span className={'font-semibold'}>
                {formatMoney(statsData?.balance)}
              </span>
            </div>
          </div>

          {getAutoLiquidatedMessage(statsData) ? (
            <div className={'bg-red-100 text-red-800 p-3 rounded text-sm'}>
              {getAutoLiquidatedMessage(statsData)}
            </div>
          ) : null}

          <div>
            <p className={'text-center font-semibold mb-2'}>
              Daily Trade Volume vs Contracts
            </p>
            {loading ? (
              <div className={'h-[320px] flex items-center justify-center text-muted-foreground'}>
                Loading...
              </div>
            ) : (
              <DailyTradeChart data={tradeData} height={320} />
            )}
          </div>

          <div>
            <p className={'text-center font-semibold mb-2'}>
              Daily Net Profit / Loss vs Account Balance
            </p>
            {loading ? (
              <div className={'h-[320px] flex items-center justify-center text-muted-foreground'}>
                Loading...
              </div>
            ) : (
              <DailyBalanceChart data={balanceData} height={320} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};